import { HttpClient, HttpHeaders } from "@angular/common/http";
import { RestApiService } from "./rest-api.service";
import { Injectable } from "@angular/core";
import { DeviceSignalingOccurrence, HorizontalSignalingOccurrence, VerticalSignalingOccurrence } from "src/models";
import { AuthService } from "./auth.service";
import { environment } from "src/environments/environment";
import { OccurrenceStatusEnum } from "src/enumerators";
import { ToastService } from "./toast.service";

@Injectable({
  providedIn: 'root'
})
export class OccurrenceService extends RestApiService {

  private _urlDeviceSignalings: string = '/device-signaling-occurrences';
  private _urlDeviceSignaling: string = '/device-signaling-occurrences/:deviceId';
  private _urlHorizontalSignalings: string = '/horizontal-signaling-occurrences';
  private _urlHorizontalSignaling: string = '/horizontal-signaling-occurrences/:horizontalId';
  private _urlVerticalSignalings: string = '/vertical-signaling-occurrences';
  private _urlVerticalSignaling: string = '/vertical-signaling-occurrences/:verticalId';
  private _urlDeviceSignalingFiles: string = '/device-signaling-occurrences/:deviceId/files';
  private _urlDeviceSignalingRemoveFile: string = '/device-signaling-occurrences/files/:fileId';
  private _urlHorizontalSignalingFiles: string = '/horizontal-signaling-occurrences/:horizontalId/files';
  private _urlHorizontalSignalingRemoveFile: string = '/horizontal-signaling-occurrences/files/:fileId';
  private _urlVerticalSignalingFiles: string = '/vertical-signaling-occurrences/:verticalId/files';
  private _urlVerticalSignalingRemoveFile: string = '/vertical-signaling-occurrences/files/:fileId';

  reqHeaders = new HttpHeaders({
    Authorization: `Bearer ${this._auth.getJwtToken()}`,
    FormData: 'true',
  });

  environmentEndpoint = environment.endpoint();

  constructor(
    private _http: HttpClient,
    private _auth: AuthService,
    private _toastService: ToastService
  ) {
    super(_http);
  };

  //Dispositivos
  getDeviceOccurrences(filter?: { occurrencesStatus: Array<OccurrenceStatusEnum> }, onlyOpenOccurrences?: boolean) {
    let filterQuery = '';
    if (onlyOpenOccurrences) {
      filter = {occurrencesStatus: []};
      filter.occurrencesStatus = [
        OccurrenceStatusEnum.Review,
        OccurrenceStatusEnum.Started,
        OccurrenceStatusEnum.ToStart
      ]
    }

    if (filter?.occurrencesStatus?.length) {
      filterQuery = `?status=${JSON.stringify(filter.occurrencesStatus)}`;
    }
    const url = this._urlDeviceSignalings + filterQuery;
    return this.get(url, DeviceSignalingOccurrence);
  }

  getDeviceOccurrence(occurrenceId: string) {
    const url = this._urlDeviceSignaling.replace(':deviceId', occurrenceId);
    return this.get(url, DeviceSignalingOccurrence);
  }

  createDeviceOccurrence(deviceOccurrence: DeviceSignalingOccurrence) {
    const url = this._urlDeviceSignalings;
    return this.post(url, deviceOccurrence, DeviceSignalingOccurrence);
  }

  updateDeviceOccurrence(deviceOccurrence: DeviceSignalingOccurrence) {
    const url = this._urlDeviceSignaling.replace(':deviceId', deviceOccurrence.id);
    return this.put(url, deviceOccurrence, DeviceSignalingOccurrence);
  }

  uploadDeviceFiles(files: any[], occurenceId: string) {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    const url = this.environmentEndpoint + this._urlDeviceSignalingFiles.replace(':deviceId', occurenceId);
    return this._http.post(url, formData, { headers: this.reqHeaders });
  }

  removeDeviceFile(fileId: string) {
    const url = this._urlDeviceSignalingRemoveFile.replace(':fileId', fileId);
    return this.del(url, DeviceSignalingOccurrence);
  }

  //Sinalização Vertical
  getVerticalOccurrences(filter?: { occurrencesStatus: Array<OccurrenceStatusEnum> }, onlyOpenOccurrences?: boolean) {
    let filterQuery = '';
    if (onlyOpenOccurrences) {
      filter = {occurrencesStatus: []};
      filter.occurrencesStatus = [
        OccurrenceStatusEnum.Review,
        OccurrenceStatusEnum.Started,
        OccurrenceStatusEnum.ToStart
      ];
    }

    if (filter?.occurrencesStatus?.length) {
      filterQuery = `?status=${JSON.stringify(filter.occurrencesStatus)}`;
    }

    const url = this._urlVerticalSignalings + filterQuery;
    return this.get(url, VerticalSignalingOccurrence);
  }

  getVerticalOccurrence(occurrenceId: string) {
    const url = this._urlVerticalSignaling.replace(':verticalId', occurrenceId);
    return this.get(url, VerticalSignalingOccurrence);
  }

  createVerticalOccurrence(verticalOccurrence: VerticalSignalingOccurrence) {
    const url = this._urlVerticalSignalings;
    return this.post(url, verticalOccurrence, VerticalSignalingOccurrence);
  }

  updateVerticalOccurrence(verticalOccurrence: VerticalSignalingOccurrence) {
    const url = this._urlVerticalSignaling.replace(':verticalId', verticalOccurrence.id);
    return this.put(url, verticalOccurrence, VerticalSignalingOccurrence);
  }

  uploadVerticalFiles(files: any[], occurenceId: string) {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    const url = this.environmentEndpoint + this._urlVerticalSignalingFiles.replace(':verticalId', occurenceId);
    return this._http.post(url, formData, { headers: this.reqHeaders });
  }

  removeVerticalFile(fileId: string) {
    const url = this._urlVerticalSignalingRemoveFile.replace(':fileId', fileId);
    return this.del(url, VerticalSignalingOccurrence);
  }

  //Sinalização Horizontal
  getHorizontalOccurrences(filter?: { occurrencesStatus: Array<OccurrenceStatusEnum> }, onlyOpenOccurrences?: boolean) {
    let filterQuery = '';
    if (onlyOpenOccurrences) {
        filter = {occurrencesStatus: []};
        filter.occurrencesStatus = [
        OccurrenceStatusEnum.Review,
        OccurrenceStatusEnum.Started,
        OccurrenceStatusEnum.ToStart
      ]
    }

    if (filter?.occurrencesStatus?.length) {
      filterQuery = `?status=${JSON.stringify(filter.occurrencesStatus)}`;
    }

    const url = this._urlHorizontalSignalings + filterQuery;
    return this.get(url, HorizontalSignalingOccurrence);
  }

  getHorizontalOccurrence(occurrenceId: string) {
    const url = this._urlHorizontalSignaling.replace(':horizontalId', occurrenceId);
    return this.get(url, HorizontalSignalingOccurrence);
  }

  createHorizontalOccurrence(horizontalOccurrence: HorizontalSignalingOccurrence) {
    const url = this._urlHorizontalSignalings;
    return this.post(url, horizontalOccurrence, HorizontalSignalingOccurrence);
  }

  updateHorizontalOccurrence(horizontalOccurrence: HorizontalSignalingOccurrence) {
    const url = this._urlHorizontalSignaling.replace(':horizontalId', horizontalOccurrence.id);
    return this.put(url, horizontalOccurrence, HorizontalSignalingOccurrence);
  }

  uploadHorizontalFiles(files: any[], occurenceId: string) {
    const formData = new FormData();
    for (const file of files) {
      formData.append('files', file);
    }
    const url = this.environmentEndpoint + this._urlHorizontalSignalingFiles.replace(':horizontalId', occurenceId);
    return this._http.post(url, formData, { headers: this.reqHeaders });
  }

  removeHorizontalFile(fileId: string) {
    const url = this._urlHorizontalSignalingRemoveFile.replace(':fileId', fileId);
    return this.del(url, HorizontalSignalingOccurrence);
  }

  //Valida especificações do vídeo
  videoValidation(file: File): Promise<boolean> {
    const MAX_SIZE = 70000000; //70mb
    const MAX_DURATION = 10; //10segundos

    return new Promise((res, rej) => {
      const video = document.createElement('video');
      video.src = URL.createObjectURL(file);
      let videoValidation = false;
  
      video.onloadedmetadata = () => {
        if (video.duration <= MAX_DURATION && file.size < MAX_SIZE) {
          videoValidation = true
        } else if (video.duration > MAX_DURATION) {
          this._toastService.showError('O vídeo deve ter duração inferior a 10s.');
        } else {
          this._toastService.showError('O vídeo deve ter tamanho inferior a 70MB.');
        }
  
        // Limpar recursos
        URL.revokeObjectURL(video.src);
        res(videoValidation);
      };
    })
  }
}